import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter } from "next/router";
import useSWR from "swr";

import { useCallback, useMemo } from "react";

import classNames from "classnames";
import truncate from "lodash/truncate";
import { Insight } from "models/insight";
import { twMerge } from "tailwind-merge";

import { faSpinner } from "@fortawesome/free-solid-svg-icons/faSpinner";
import { faTimes } from "@fortawesome/free-solid-svg-icons/faTimes";

import { Title } from "components/customElements";
import { SKSpinner } from "components/customElements/skSpinner";
import { entityInsightsUrl } from "components/EntityPageLayout/EntityPageAnalysis/EntityPageInsightsList";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import RichContent from "components/RichContent";
import ShareButton from "components/ShareButton";
import TopNavBarMobile from "components/TopNavBar/TopNavBarMobile";
import useActivityTracker from "hooks/useActivityTracker";
import { useEffectOnce } from "hooks/useEffectOnce";
import useMediaQuery from "hooks/useMediaQuery";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";

import styles from "./style.module.scss";

const Modal = dynamic(() => import("react-modal"));

export const earningsUrl = (id: string | number) => {
  const url = new ApiEndpoint(`/api/v2/entity-earnings-alerts/${id}`);
  url.searchParams.append("include", "entity");

  return url.href;
};

type EarningsAlertModalProps = {
  id: number | string;
  onClose?: () => void;
};

const EarningsAlertModal = ({
  id,
  onClose: customOnClose,
}: EarningsAlertModalProps) => {
  const router = useRouter();
  const { mobile } = useMediaQuery();
  const { trackActivity } = useActivityTracker();

  const { data: earnings, isLoading } = useSWR(earningsUrl(id), baseFetcher);

  const { data: insights, isValidating } = useSWR(
    entityInsightsUrl(
      router.query.slug as string,
      1,
      "",
      undefined,
      false,
      "insights",
      "5"
    ),
    baseFetcher
  );

  useEffectOnce((ack) => {
    ack();
    trackActivity("earnings.view", {
      id: id,
      type: "entity-earnings-alerts",
    });
  }, []);

  const onClose = useCallback(() => {
    if (customOnClose) {
      return customOnClose();
    }
    if (router.query.slug) {
      router.push(
        `/entities/${router.query.slug}/announcements?tab=earnings-alerts`,
        undefined,
        { scroll: false }
      );
    } else {
      router.back();
    }
  }, [customOnClose, router]);
  const url = router.asPath;
  const clipboardText = `${earnings?.entity?.shortName} | ${earnings?.title}: ${url}`;
  const whatsappText = `Check out this ${clipboardText}`;
  const twitterText = truncate(clipboardText, { length: 100 });
  const shareBtnOptions = {
    email: {
      link: `mailto:?subject=✪ Smartkarma | earnings: | ${earnings?.title}&body=${whatsappText}`,
    },
    clipboard: {
      message: clipboardText,
    },
    privateMessage: {
      message: `Check out this <a href="${url}">${earnings?.entity?.shortName} | ${earnings?.title}</a>`,
    },
    whatsapp: {
      link: `https://web.whatsapp.com/send?text=${whatsappText}`,
    },
    twitter: {
      link: `https://twitter.com/share?url=${url}&text=${twitterText}&via=smartkarma`,
    },
    linkedIn: {
      link: `https://www.linkedin.com/shareArticle?mini=true&url=${url}&source=Smartkarma`,
    },
  };

  const isEmpty = !isValidating && !insights?.data?.length;

  const renderItem = useCallback((item: Insight) => {
    return (
      <Link
        href={`/insights/${item.slug}`}
        className="text-subtle-black text-[13px] !font-pt-serif"
      >
        {item.tagline}
      </Link>
    );
  }, []);

  const list = useMemo(
    () =>
      isEmpty ? (
        <div className="italic text-shady-gray text-[13px]">No items found</div>
      ) : isValidating ? (
        <SKSpinner />
      ) : (
        <div className="flex flex-col gap-[5px]">
          {insights?.data?.map(renderItem)}
        </div>
      ),
    [insights?.data, isEmpty, isValidating, renderItem]
  );

  return (
    <Modal
      portalClassName={styles.modal}
      overlayClassName={styles.overlay}
      className={classNames(
        styles.content,
        mobile ? styles.mobileLayout : styles.desktopLayout
      )}
      isOpen
      onRequestClose={onClose}
      onAfterOpen={() => {
        document.body.style.overflowY = "hidden";
      }}
      onAfterClose={() => {
        document.body.style.overflowY = "";
      }}
    >
      {mobile ? (
        <TopNavBarMobile
          onGoBack={onClose}
          rightActionButtons={<ShareButton options={shareBtnOptions} />}
        />
      ) : (
        <div className={styles.actionsBar}>
          <FontAwesomeIcon
            icon={faTimes}
            className={styles.closeButton}
            onClick={onClose}
          />
          <ShareButton
            className={styles.shareButton}
            options={shareBtnOptions}
          />
        </div>
      )}
      {earnings?.entity ? (
        <>
          <div className={styles.source}>
            <Link href={`/entities/${earnings.entity.slug}`} prefetch={false}>
              {earnings.entity.shortName}
            </Link>
          </div>
          <div className={styles.header}>
            <div className={styles.titleContainer}>
              <h1 className={styles.title}>{earnings.title}</h1>
            </div>
          </div>
          <div className={classNames(styles.body)}>
            <section className={twMerge(styles.section)}>
              <RichContent
                html={`
                  ${earnings.bodyListicle
                    .replace(/```html/g, "")
                    .replace(/```/g, "")}
                `}
              />
            </section>
          </div>
          <div className="">
            <Title className="flex">
              <div className="flex-1">Related Insights</div>
              <a
                href={`/entities/${router.query?.slug}/research`}
                onClick={onClose}
                className="pull-right normal-case text-smart-teal"
              >
                More »
              </a>
            </Title>
            {list}
          </div>
        </>
      ) : isLoading ? (
        <div className={styles.loading}>
          <div className="flex items-center justify-center">
            <FontAwesomeIcon
              icon={faSpinner}
              className="w-[30px] h-[30px] text-inactive-gray rounded-full animate-spin"
            />
          </div>
        </div>
      ) : (
        <div className={styles.error}>
          <div className="flex items-center justify-center">
            <div className="text-center text-[15px] text-shady-gray">
              No earnings found
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default EarningsAlertModal;
